import { useEffect, useState } from "react";
import { Form, Input, DatePicker, Select, Radio, Button, Checkbox } from "antd";
import { useTranslation } from "../../../../utils/onboardingI18n";
import { GetAllCountryApi } from "../../../../api/request/common.js";
import { getTaxDomicilesReasons } from "../../../../api/request/corporate.js";
import Loader from "../../../../components/Loader.jsx";
import {
  AlternativeMobileNumberValidator,
  mobileNumberValidator,
} from "../../../../utils/utils.js";
import {
  addUpdateAccountInformation,
  getPersonalInfo,
} from "../../../../api/request/individualOnboarding.js";
import { useSelector } from "react-redux";
import { handleApiError } from "../../../../utils/handleApiError.js";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import AlternateTaxIdentifierField from "../../../../components/onboarding/AlternateTaxIdentifierField.jsx";
import OnboardingMultiSelect from "../../../../components/onboarding/OnboardingMultiSelect.jsx";

const { TextArea } = Input;

const AccountInformation = ({ onNext, onPrevious, isSubmitted, id }) => {
  const { t } = useTranslation("onBoarding");
  const { userDetails } = useSelector((state) => state.user);
  const [form] = Form.useForm();
  const [countries, setCountries] = useState([]);
  const [countryCodes, setCountryCodes] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isOtherNational, setIsOtherNational] = useState(false);
  const [isUSPerson, setIsUSPerson] = useState(null);
  const [isPEP, setIsPEP] = useState(null);
  const [accountInfoId, setAccountInfoId] = useState(null);
  const isDisabled = isSubmitted;
  const isOtherNationalValue = Form.useWatch("isAnotherNationality", form);
  const isUSPersonValue = Form.useWatch("areYouUSPerson", form);
  const isMultiTaxDomicile = Form.useWatch("isMultiTaxDomicileEnabled", form);
  const isPEPValue = Form.useWatch("isPoliticallyExposedPerson", form);

  useEffect(() => {
    setIsOtherNational(isOtherNationalValue === true);
  }, [isOtherNationalValue]);

  useEffect(() => {
    setIsUSPerson(isUSPersonValue);
  }, [isUSPersonValue]);

  useEffect(() => {
    setIsPEP(isPEPValue);
  }, [isPEPValue]);

  const handleUSPersonChange = (e) => {
    const value = e.target.value;
    setIsUSPerson(value);
    if (!value) {
      form.setFieldsValue({ acceptDeclaration: false });
    }
  };

  const handlePEPChange = (e) => {
    const value = e.target.value;
    setIsPEP(value);
    if (!value) {
      form.setFieldsValue({ pepDetails: "" });
    }
  };

  const getAllCountries = async () => {
    try {
      setIsLoading(true);
      const { data } = await GetAllCountryApi();
      if (data?.success) {
        setCountries(
          data?.data?.map((item) => ({
            value: item?.id,
            label: item?.countryName,
          }))
        );

        setCountryCodes(
          data?.data
            ?.map((item) => ({
              value: (item?.dialCode || item?.countryCode1),
              label: `${(item?.dialCode || item?.countryCode1)} (${item?.countryCode2})`,
              countryCode: (item?.dialCode || item?.countryCode1),
            }))
            ?.filter((item) => item?.value)
        );
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getAllReasons = async () => {
    try {
      const { data } = await getTaxDomicilesReasons();
      if (data?.success) {
        setReasons(
          data?.data?.map((item) => ({ value: item?.id, label: item?.name }))
        );
      }
    } catch (error) {
      console.error("Error fetching tax domicile reasons:", error);
    }
  };

  const loadPersonInfo = async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      const { data } = await getPersonalInfo(id);
      if (data?.success && data?.data) {
        const personalInfo = data.data;

        const phoneNumber = personalInfo.phoneNumber || "";
        const [mobileCountryCode, ...mobileNumberParts] = phoneNumber
          .trim()
          .split(" ");
        const mobileNumber = mobileNumberParts.join(" ") || "";

        const alternatePhoneNumber = personalInfo.alternatePhoneNumber || "";
        const [alternateCountryCode, ...alternateNumberParts] =
          alternatePhoneNumber.trim().split(" ");
        const alternatePhoneNumberValue = alternateNumberParts.join(" ") || "";

        const dob = personalInfo.dob ? dayjs(personalInfo.dob) : null;
        
        form.setFieldsValue({
          acceptDeclaration: true,
          title: personalInfo.title || undefined,
          firstName: personalInfo.firstName || "",
          lastName: personalInfo.lastName || "",
          dob: dob,
          placeOfBirth: personalInfo.placeOfBirth || "",
          gender: personalInfo.gender || "",
          nationalityId: personalInfo.nationalityId || undefined,
          isAnotherNationality: personalInfo.isAnotherNationality || false,
          multiNationalityIds: personalInfo.multiNationalityIds || [],
          address: personalInfo.address || "",
          addressLine2: personalInfo.addressLine2 || "",
          apartmentVillaInfo: personalInfo.apartmentVillaInfo || "",
          streetAreaInfo: personalInfo.streetAreaInfo || "",
          residenceCountryId: personalInfo.residenceCountryId || undefined,
          cityTown: personalInfo.cityTown || "",
          mobileCountryCode: mobileCountryCode || undefined,
          mobileNumber: mobileNumber || "",
          alternateCountryCode: alternateCountryCode || undefined,
          alternatePhoneNumber: alternatePhoneNumberValue || "",
          postalZipCode: personalInfo.postalZipCode || "",
          areYouUSPerson: personalInfo.areYouUSPerson || false,
          isMultiTaxDomicileEnabled:
            personalInfo.isMultiTaxDomicileEnabled || false,
          taxDomiciles:
            personalInfo.taxDomiciles && personalInfo.taxDomiciles.length > 0
              ? personalInfo.taxDomiciles.map((td) => ({
                  id: td.id,
                  taxDomicileCountryId: td.taxDomicileCountryId || null,
                  taxIDNumber: td.taxIDNumber || "",
                  isTINIDAvailable: td.isTINIDAvailable || false,
                  taxDomicileReasonID: td.taxDomicileReasonID || null,
                  taxDomicileRegEx: td.taxDomicileRegEx || "",
                  alternateTaxIdentifier: td.alternateTaxIdentifier || "",
                }))
              : [
                  {
                    taxDomicileCountryId: null,
                    taxIDNumber: "",
                    isTINIDAvailable: false,
                    taxDomicileReasonID: null,
                    alternateTaxIdentifier: "",
                  },
                ],
          isPoliticallyExposedPerson:
            personalInfo.isPoliticallyExposedPerson || false,
          pepDetails: personalInfo.pepDetails || "",
        });

        setAccountInfoId(personalInfo.id);
      }
    } catch (error) {
      console.error("Error loading personal info:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        await Promise.all([getAllCountries(), getAllReasons()]);
        if (id) {
          await loadPersonInfo();
        }
      } catch (error) {
        console.error("Error initializing:", error);
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (id && countries.length > 0) {
      loadPersonInfo();
    }
  }, [id]);

  const handleMultiTaxResidentChange = (e) => {
    const isMultiTaxDomicile = e?.target?.value;
    const taxDomiciles = form.getFieldValue("taxDomiciles") || [];

    if (isMultiTaxDomicile) {
      if (!taxDomiciles?.length) {
        form.setFieldsValue({
          taxDomiciles: [
            {
              taxDomicileCountryId: null,
              taxIDNumber: "",
              isTINIDAvailable: false,
              taxDomicileReasonID: null,
            },
          ],
        });
      }
    } else {
      if (taxDomiciles?.length > 1) {
        form.setFieldsValue({
          taxDomiciles: [taxDomiciles[0]],
        });
      }
    }
  };

  const filterOption = (input, option) => {
    return (option?.label ?? "").toLowerCase().includes(input.toLowerCase());
  };

  const handleSubmit = async (values) => {
    if (isDisabled) return;
    try {
      setIsSubmitLoading(true);

      const dob =
        values.dob && dayjs(values.dob).isValid()
          ? dayjs(values.dob).toISOString()
          : null;

      const phoneNumber =
        values.mobileCountryCode && values.mobileNumber
          ? `${values.mobileCountryCode} ${values.mobileNumber}`.trim()
          : "";

      const alternatePhoneNumber =
        values.alternateCountryCode && values.alternatePhoneNumber
          ? `${values.alternateCountryCode} ${values.alternatePhoneNumber}`.trim()
          : "";

      const taxDomiciles = (values.taxDomiciles || []).map((item) => {
        const taxDomicile = {
          taxDomicileCountryId: item.taxDomicileCountryId || 0,
          taxIDNumber: item.taxIDNumber || "",
          isTINIDAvailable: item.isTINIDAvailable || false,
          taxDomicileReasonID: item.taxDomicileReasonID || 0,
          taxDomicileRegEx: item.taxDomicileRegEx || "",
          alternateTaxIdentifier: item.alternateTaxIdentifier || "",
        };
        if (item.id) {
          taxDomicile.id = item.id;
        }
        return taxDomicile;
      });

      const payload = {
        // userId: userDetails?._id || "",
        firstName: values.firstName || "",
        lastName: values.lastName || "",
        dob: dob,
        placeOfBirth: values.placeOfBirth || "",
        gender: values.gender || "",
        nationalityId: values.nationalityId || 0,
        isAnotherNationality: values.isAnotherNationality || false,
        multiNationalityIds: values.multiNationalityIds || [],
        address: values.address || "",
        addressLine2: values.addressLine2 || "",
        apartmentVillaInfo: values.apartmentVillaInfo || "",
        streetAreaInfo: values.streetAreaInfo || "",
        residenceCountryId: values.residenceCountryId || 0,
        cityTown: values.cityTown || "",
        phoneNumber: phoneNumber,
        alternatePhoneNumber: alternatePhoneNumber,
        postalZipCode: values.postalZipCode || "",
        areYouUSPerson: values.areYouUSPerson || false,
        taxDomiciles: taxDomiciles,
        isPoliticallyExposedPerson: values.isPoliticallyExposedPerson || false,
        pepDetails: values.pepDetails || "",
      };

      if (values.title) {
        payload.title = values.title;
      }

      if (values.refferedById) {
        payload.refferedById = values.refferedById;
      }

      if (id) {
        payload.id = id;
      } else if (accountInfoId) {
        payload.id = accountInfoId;
      }

      const { data } = await addUpdateAccountInformation(payload);

      if (data?.success) {
        toast.success(
          data?.message || t("Account information saved successfully")
        );
        if (data?.data?.id) {
          setAccountInfoId(data.data.id);
        }
        onNext();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitLoading(false);
    }
  };

  if (isLoading) return <Loader />;

  return (
    <div className="max-w-5xl mx-auto p-6">
      <Form
        form={form}
        size="large"
        layout="vertical"
        autoComplete="off"
        disabled={isDisabled}
        onFinish={handleSubmit}
        initialValues={{
          isAnotherNationality: false,
          isMultiTaxDomicileEnabled: false,
          taxDomiciles: [
            {
              taxDomicileCountryId: null,
              taxIDNumber: "",
              isTINIDAvailable: false,
              taxDomicileReasonID: null,
            },
          ],
        }}
      >
        <div className="border border-gray-300 rounded-lg p-6">
          <h2 className="text-2xl font-semibold mb-6">{t("Personal information")}</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-4">
              <Form.Item
                label={<span>{t("First Name/Given Name")}</span>}
                name="firstName"
                rules={[{ required: true, message: t("Please enter first name") }]}
              >
                <Input placeholder={t("Enter first name")} />
              </Form.Item>

              <Form.Item
                label={<span>{t("Date Of Birth")}</span>}
                name="dob"
                rules={[
                  { required: true, message: t("Please select date of birth") },
                ]}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  placeholder="DD/MM/YYYY"
                  className="w-full"
                  style={{ width: "100%" }}
                  disabledDate={(current) =>
                    current && current > dayjs().endOf("day")
                  }
                />
              </Form.Item>

              <Form.Item
                label={<span>{t("Place Of Birth")}</span>}
                name="placeOfBirth"
                rules={[
                  { required: true, message: t("Please enter place of birth") },
                ]}
              >
                <Input placeholder={t("Enter place of birth")} />
              </Form.Item>

              <Form.Item
                label={t("Are you a national of any other country?")}
                name="isAnotherNationality"
                rules={[{ required: true, message: t("Please select an option") }]}
              >
                <Radio.Group>
                  <Radio value={true}>{t("Yes")}</Radio>
                  <Radio value={false}>{t("No")}</Radio>
                </Radio.Group>
              </Form.Item>
            </div>

            <div className="space-y-4">
              <Form.Item
                label={<span>{t("Last Name/Surname")}</span>}
                name="lastName"
                rules={[{ required: true, message: t("Please enter last name") }]}
              >
                <Input placeholder={t("Enter last name")} />
              </Form.Item>

              <Form.Item
                label={<span>{t("Gender")}</span>}
                name="gender"
                rules={[{ required: true, message: t("Please select gender") }]}
              >
                <Radio.Group>
                  <Radio value="Male">{t("Male")}</Radio>
                  <Radio value="Female">{t("Female")}</Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                label={<span>{t("Nationality 1")}</span>}
                name="nationalityId"
                rules={[
                  { required: true, message: t("Please select nationality") },
                ]}
              >
                <Select
                  showSearch
                  placeholder={t("Select Country")}
                  filterOption={filterOption}
                  options={countries}
                />
              </Form.Item>
            </div>
          </div>

          {isOtherNational && (
            <Form.Item
              label={t("Select Other Nationalities")}
              name="multiNationalityIds"
              rules={[
                {
                  required: true,
                  message: t("Please select at least one country"),
                },
              ]}
              className="mt-4"
            >
              <OnboardingMultiSelect
                showSearch
                placeholder={t("Select countries")}
                filterOption={filterOption}
                options={countries}
              />
            </Form.Item>
          )}
        </div>

        <div className="border border-gray-300 rounded-lg p-6 mt-6">
          <h2 className="text-2xl font-semibold mb-2">
            {t("Current Residential Address")}
          </h2>
          <p className="text-sm text-gray-600 mb-6">
            {t("As it appears on your proof of residence document")}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              label={
                <span>
                  {t("Current Residential address (As it appears on your proof of residence document)")}{" "}
                </span>
              }
              name="address"
              rules={[
                {
                  required: true,
                  message: t("Please enter current residential address"),
                },
              ]}
              className="md:col-span-2"
            >
              <TextArea
                rows={4}
                placeholder={t("Enter current residential address")}
              />
            </Form.Item>

            <Form.Item
              label={t("Address Line 2")}
              name="addressLine2"
              className="md:col-span-2"
            >
              <TextArea rows={4} placeholder={t("Enter address line 2")} />
            </Form.Item>

            <Form.Item
              label={<span>{t("Building and Apartment Number")} </span>}
              name="apartmentVillaInfo"
              rules={[
                {
                  required: true,
                  message: t("Please enter building and apartment number"),
                },
              ]}
            >
              <Input placeholder={t("Enter building and apartment number")} />
            </Form.Item>

            <Form.Item
              label={<span>{t("Street Name")}</span>}
              name="streetAreaInfo"
              rules={[{ required: true, message: t("Please enter street name") }]}
            >
              <Input placeholder={t("Enter street name")} />
            </Form.Item>

            <Form.Item
              label={<span>{t("Country")}</span>}
              name="residenceCountryId"
              rules={[{ required: true, message: t("Please select country") }]}
            >
              <Select
                showSearch
                placeholder={t("Select Country")}
                filterOption={filterOption}
                options={countries}
              />
            </Form.Item>

            <Form.Item
              label={<span>{t("City")}</span>}
              name="cityTown"
              rules={[{ required: true, message: t("Please enter city") }]}
            >
              <Input placeholder={t("Enter city")} />
            </Form.Item>

            <Form.Item label={<span>{t("Mobile Number")}</span>} required>
              <div className="flex w-full gap-2">
                <Form.Item
                  name="mobileCountryCode"
                  noStyle
                  rules={[{ required: true, message: t("Country code required") }]}
                >
                  <Select
                    showSearch
                    placeholder={t("Code")}
                    filterOption={filterOption}
                    options={countryCodes}
                    style={{ width: "30%" }}
                  />
                </Form.Item>
                <Form.Item
                  name="mobileNumber"
                  noStyle
                  dependencies={["mobileCountryCode"]}
                  rules={[mobileNumberValidator(t)]}
                >
                  <Input
                    placeholder={t("Enter mobile number")}
                    maxLength={15}
                    inputMode="numeric"
                    onKeyPress={(e) => {
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                </Form.Item>
              </div>
            </Form.Item>

            <Form.Item label={t("Alternative Phone Number")}>
              <div className="flex w-full gap-2">
                <Form.Item name="alternateCountryCode" noStyle>
                  <Select
                    showSearch
                    placeholder={t("Code")}
                    filterOption={filterOption}
                    options={countryCodes}
                    style={{ width: "30%" }}
                  />
                </Form.Item>
                <Form.Item
                  name="alternatePhoneNumber"
                  noStyle
                  dependencies={["alternateCountryCode"]}
                  rules={[AlternativeMobileNumberValidator(t)]}
                >
                  <Input
                    placeholder="e.g. +91 81234 56789"
                    maxLength={15}
                    inputMode="numeric"
                    onKeyPress={(e) => {
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                </Form.Item>
              </div>
            </Form.Item>

            <Form.Item
              label={t("PO Box")}
              name="postalZipCode"
              className="md:col-span-2"
            >
              <Input placeholder={t("Enter PO Box")} />
            </Form.Item>
          </div>
        </div>

        <div className="border border-gray-300 rounded-lg p-6 mt-6">
          <h2 className="text-2xl font-semibold mb-2">{t("FATCA and CRS")}</h2>
          <p className="text-sm text-gray-600 mb-4">
            {t("In accordance with the Foreign Account Tax Compliance Act, 2010 (FATCA), please answer the following questions:")}
          </p>

          <Form.Item
            label={
              <span className="font-semibold">{t("Are you a U.S. Person?")}</span>
            }
            name="areYouUSPerson"
            rules={[{ required: true, message: t("Please select an option") }]}
          >
            <Radio.Group onChange={handleUSPersonChange}>
              <Radio value={true}>{t("Yes")}</Radio>
              <Radio value={false}>{t("No")}</Radio>
            </Radio.Group>
          </Form.Item>

          {isUSPerson !== null && (
            <div className="mt-4">
              <p className="text-sm text-gray-600 mb-4">
                {t("Please note this also includes if you are a US green Card holder, hold a US bank account, have a US PO box or postal address, have a US based power of attorney etc. Please visit the website www.irs.gov for full details or consult a foreign tax advisor.")}
              </p>

              {/* Declaration Box */}
              <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 mb-4">
                <p className="text-sm text-gray-700 mb-3">
                  {isUSPerson
                    ? t("I declare that I AM a U.S. Person and am subject to U.S. federal income taxes. I, hereby represent that, and by checking the applicable box below, the information provided by me is true and accurate.")
                    : t("I declare that I AM not a U.S. Person and am subject to U.S. federal income taxes. I, hereby represent that, and by checking the applicable box below, the information provided by me is true and accurate.")}
                </p>
                <Form.Item
                  name="acceptDeclaration"
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_, value) =>
                        value
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error(t("Please accept the declaration"))
                            ),
                    },
                  ]}
                >
                  <Checkbox>{t("I accept above declaration")}</Checkbox>
                </Form.Item>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6">
          <p className="text-sm text-gray-600 !font-normal mb-4">
            {t("For Common Reporting Standard (CRS) purposes, please mention which countries you are tax resident of?")}{" "}
          </p>
          <div className="border border-gray-300 rounded-lg p-6 mt-6">
            <div className="mb-6">
              <Form.Item
                label={t("Are there any other countries you are tax resident of?")}
                name="isMultiTaxDomicileEnabled"
                rules={[{ required: true, message: t("Please select an option") }]}
              >
                <Radio.Group onChange={handleMultiTaxResidentChange}>
                  <Radio value={true}>{t("Yes")}</Radio>
                  <Radio value={false}>{t("No")}</Radio>
                </Radio.Group>
              </Form.Item>
            </div>

            <Form.List name="taxDomiciles">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <div
                      key={field.key}
                      className="border border-gray-300 rounded-lg p-6 mt-6"
                    >
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold">
                          {index === 0
                            ? t("Tax Resident Information")
                            : `${t("Additional Tax Resident Information")} ${index}`}
                        </h3>

                        {index !== 0 && (
                          <Button
                            type="link"
                            danger
                            onClick={() => remove(field.name)}
                            disabled={isDisabled}
                          >
                            {t("Remove")}
                          </Button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                          label={<span>{t("Select Country")}</span>}
                          name={[field.name, "taxDomicileCountryId"]}
                          rules={[
                            {
                              required: true,
                              message: t("Please select country"),
                            },
                          ]}
                        >
                          <Select
                            showSearch
                            placeholder={t("Select country")}
                            filterOption={filterOption}
                            options={countries}
                            disabled={isDisabled}
                          />
                        </Form.Item>

                        <Form.Item shouldUpdate noStyle>
                          {({ getFieldValue }) => {
                            const noTIN = getFieldValue([
                              "taxDomiciles",
                              index,
                              "isTINIDAvailable",
                            ]);

                            return (
                              <Form.Item
                                label={t("Tax Identification Number")}
                                name={[field.name, "taxIDNumber"]}
                                rules={
                                  noTIN
                                    ? []
                                    : [
                                        {
                                          required: true,
                                          message: t("Enter TIN"),
                                        },
                                      ]
                                }
                                className="w-full"
                                disabled={noTIN || isDisabled}
                              >
                                <Input
                                  placeholder={t("Enter tax identification number")}
                                  disabled={noTIN || isDisabled}
                                />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>

                        <Form.Item
                          name={[field.name, "isTINIDAvailable"]}
                          valuePropName="checked"
                          className="md:col-span-2"
                        >
                          <Checkbox disabled={isDisabled}>
                            {t("Check if you don't have TIN?")}
                          </Checkbox>
                        </Form.Item>

                        <Form.Item shouldUpdate noStyle>
                          {({ getFieldValue }) => {
                            const isTIN = !getFieldValue([
                              "taxDomiciles",
                              index,
                              "isTINIDAvailable",
                            ]);

                            if (isTIN) return null;

                            return (
                              <Form.Item
                                label={t("Please select a reason")}
                                name={[field.name, "taxDomicileReasonID"]}
                                rules={[
                                  {
                                    required: true,
                                    message: t("Please select reason"),
                                  },
                                ]}
                                className="md:col-span-2"
                              >
                                <Radio.Group disabled={isDisabled}>
                                  {reasons.map((r) => (
                                    <Radio
                                      key={r.value}
                                      value={r.value}
                                      className="block mb-2"
                                    >
                                      {r.label}
                                    </Radio>
                                  ))}
                                </Radio.Group>
                              </Form.Item>
                            );
                          }}
                        </Form.Item>

                        <AlternateTaxIdentifierField
                          field={field}
                          index={index}
                          isDisabled={isDisabled}
                          label={t("ID or Passport Number")}
                          t={t}
                        />
                      </div>
                    </div>
                  ))}

                  {isMultiTaxDomicile && (
                    <div className="mt-6">
                      <Button
                        type="dashed"
                        block
                        onClick={() =>
                          add({
                            taxDomicileCountryId: null,
                            taxIDNumber: "",
                            isTINIDAvailable: false,
                            taxDomicileReasonID: null,
                            alternateTaxIdentifier: "",
                          })
                        }
                        disabled={isDisabled}
                      >
                        {t("+ Add Another Tax Resident Country")}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </Form.List>
          </div>
        </div>

        <div className="border border-gray-300 rounded-lg p-6 mt-6">
          <h3 className="text-2xl font-semibold mb-4 ">
            {t("Politically Exposed Person?")}
          </h3>

          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              {t("PEP shall include Heads of States or Governments, senior politicians, senior government officials, judicial or military officials, senior executive managers of state owned corporations, and senior officials of political parties and persons who are, or have previously been, entrusted with the management of an international organisation or any prominent function within such an organisation; and the definition also includes the following:")}
            </p>

            <div className="ml-4 mb-2">
              <p className="text-sm text-gray-600 mb-1">
                <strong>{t("1. Direct family members")}</strong> {t("Of the PEP, who are spouses, children, spouses of children, parents).")}
              </p>
              <p className="text-sm text-gray-600">
                <strong>
                  {t("2. Associates known to be close to the PEP, which include:")}
                </strong>
              </p>
              <ul className="list-disc ml-6 mt-1 text-sm text-gray-600">
                <li>
                  {t("Individuals having joint ownership rights in a legal person or arrangement or any other close business relationship with the PEP.")}
                </li>
                <li>
                  {t("Individuals having individual ownership rights in a legal person or arrangement established in favour of the PEP.")}
                </li>
              </ul>
            </div>
          </div>

          <Form.Item
            name="isPoliticallyExposedPerson"
            rules={[{ required: true, message: t("Please select an option") }]}
          >
            <Radio.Group onChange={handlePEPChange} disabled={isDisabled}>
              <Radio value={true}>{t("Yes")}</Radio>
              <Radio value={false}>{t("No")}</Radio>
            </Radio.Group>
          </Form.Item>

          {isPEP && (
            <Form.Item
              label={<span>{t("Please provide details")}</span>}
              name="pepDetails"
              rules={[
                { required: true, message: t("Please provide PEP details") },
              ]}
              className="mt-4"
            >
              <TextArea
                rows={4}
                placeholder={t("Please provide details about being a Politically Exposed Person")}
                disabled={isDisabled}
              />
            </Form.Item>
          )}
        </div>

        <div className="flex justify-end gap-4 mt-6">
          {onPrevious && (
            <Button onClick={onPrevious} size="large" disabled={false}>
              {t("Previous")}
            </Button>
          )}
          {isDisabled ? (
            <Button type="primary" size="large" onClick={onNext} disabled={false}>
              {t("Next")}
            </Button>
          ) : (
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={isSubmitLoading}
              disabled={isSubmitLoading}
            >
              {isSubmitLoading ? t("Saving...") : t("Next")}
            </Button>
          )}
        </div>
      </Form>
    </div>
  );
};

export default AccountInformation;
